import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { colors, courtColors, Icon } from "../ui/index.js";
import { ORIGIN_BADGE } from "./court-chips.js";
import { assignPlannedCourts, deriveCourts, formatTimeShort, } from "./court-grid-helpers.js";
/** Cross-hatch (both diagonals) used for the Unbookable state — reads as "blocked". */
const UNBOOKABLE_HATCH = `repeating-linear-gradient(45deg, ${colors.danger}66 0 1px, transparent 1px 6px), ` +
    `repeating-linear-gradient(-45deg, ${colors.danger}66 0 1px, transparent 1px 6px)`;
/**
 * Court×time grid (main-app look): rows are start times, columns are courts.
 * COLOUR identifies the COURT (C1–C4…); the slot STATE is shown by fill:
 *   • solid court colour      → Booked (confirmed at the venue)
 *   • diagonal stripes        → Pending (planned, waiting to book)
 *   • red hatch               → Unbookable (a planned slot that won't book —
 *                               past its window / outside permissions)
 * See {@link CourtGridKey} for the legend.
 */
export function CourtGrid({ slots, courtCount, compact = false, }) {
    const booked = slots.filter((s) => s.status === "booked");
    const planned = slots.filter((s) => s.status === "planned");
    const unbookable = slots.filter((s) => s.status === "unfulfilled");
    // Show every court at the venue (1..courtCount) so the grid is the full hall,
    // not just the booked subset; derive from the slots only when count unknown.
    const courts = courtCount && courtCount > 0
        ? Array.from({ length: Math.min(courtCount, 8) }, (_, i) => i + 1)
        : deriveCourts(slots);
    const plannedCourtMap = assignPlannedCourts([...planned, ...unbookable], booked, courts);
    const times = [...new Set(slots.map((s) => s.startTime))].sort();
    if (times.length === 0)
        return null;
    const lookup = new Map();
    for (const s of booked) {
        if (s.courtNumber != null)
            lookup.set(`${s.startTime}|${s.courtNumber}`, { tone: "booked", origin: s.origin ?? "rule" });
    }
    for (const s of [...planned, ...unbookable]) {
        const court = plannedCourtMap.get(s.id);
        if (court == null)
            continue;
        const key = `${s.startTime}|${court}`;
        if (lookup.has(key))
            continue; // never overwrite a booked cell
        lookup.set(key, { tone: s.status === "unfulfilled" ? "unbookable" : "planned" });
    }
    const cellFill = (tone, color) => {
        if (tone === "booked")
            return { background: color + "1F", borderLeft: `3px solid ${color}` };
        if (tone === "unbookable")
            // Cross-hatch (both diagonals) reads as "blocked" — clearly distinct from
            // Pending's single-direction stripes (colour alone wasn't enough).
            return {
                background: `${UNBOOKABLE_HATCH}, ${colors.dangerLight}`,
                borderLeft: `3px solid ${colors.danger}66`,
            };
        // planned — single-direction stripes
        return {
            background: `repeating-linear-gradient(-45deg, ${color}06, ${color}06 5px, ${color}14 5px, ${color}14 9px)`,
            borderLeft: `3px solid ${color}40`,
        };
    };
    const cellLabel = {
        booked: "booked",
        planned: "pending",
        unbookable: "missed",
    };
    const cellH = compact ? 18 : 30;
    const cellR = compact ? 4 : 6;
    const labelFont = compact ? "0.44rem" : "0.5rem";
    return (_jsxs("div", { style: {
            display: "grid",
            gridTemplateColumns: `auto repeat(${courts.length}, 1fr)`,
            gap: compact ? 2 : 2,
        }, children: [_jsx("div", {}), courts.map((c) => (_jsxs("div", { style: {
                    textAlign: "center",
                    fontSize: compact ? "0.58rem" : "0.65rem",
                    fontWeight: 700,
                    color: courtColors[c] ?? colors.textSecondary,
                    padding: compact ? "0 0 2px" : "2px 0 4px",
                }, children: ["C", c] }, c))), times.map((time) => (_jsxs(React.Fragment, { children: [_jsx("div", { style: {
                            fontSize: compact ? "0.62rem" : "0.78rem",
                            fontWeight: 500,
                            color: compact ? colors.textSecondary : colors.text,
                            fontVariantNumeric: "tabular-nums",
                            padding: compact ? "0 6px 0 0" : "7px 8px 7px 0",
                            display: "flex",
                            alignItems: "center",
                        }, children: formatTimeShort(time) }), courts.map((c) => {
                        const cell = lookup.get(`${time}|${c}`);
                        const color = courtColors[c] ?? colors.textSecondary;
                        if (!cell) {
                            return (_jsx("div", { style: { background: colors.surfaceSecondary, borderRadius: cellR, minHeight: cellH } }, `${time}-${c}`));
                        }
                        return (_jsx("div", { title: cell.tone === "booked"
                                ? `Booked · ${ORIGIN_BADGE[cell.origin ?? "rule"].label} (${ORIGIN_BADGE[cell.origin ?? "rule"].desc})`
                                : cellLabel[cell.tone], style: {
                                ...cellFill(cell.tone, color),
                                borderRadius: cellR,
                                minHeight: cellH,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 3,
                            }, children: cell.tone === "booked" ? (_jsxs(_Fragment, { children: [_jsx(Icon, { name: ORIGIN_BADGE[cell.origin ?? "rule"].icon, size: compact ? 7 : 9, color: color }), _jsx("span", { style: {
                                            fontSize: labelFont,
                                            fontWeight: 600,
                                            color,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.04em",
                                        }, children: ORIGIN_BADGE[cell.origin ?? "rule"].label })] })) : (_jsx("span", { style: {
                                    fontSize: labelFont,
                                    fontWeight: 600,
                                    color: cell.tone === "unbookable" ? colors.danger : color + "B0",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                }, children: cellLabel[cell.tone] })) }, `${time}-${c}`));
                    })] }, time)))] }));
}
/** Pattern legend — colour = court, fill = status. */
export function CourtGridKey({ style }) {
    const swatch = (bg, border) => ({
        width: 22,
        height: 14,
        borderRadius: 3,
        background: bg,
        borderLeft: `3px solid ${border}`,
        flexShrink: 0,
    });
    const c = colors.primary;
    const items = [
        { sw: swatch(c + "1F", c), label: "Booked", desc: "confirmed at the venue" },
        {
            sw: swatch(`repeating-linear-gradient(-45deg, ${c}06, ${c}06 5px, ${c}14 5px, ${c}14 9px)`, c + "40"),
            label: "Pending",
            desc: "waiting to be booked",
        },
        {
            sw: swatch(`${UNBOOKABLE_HATCH}, ${colors.dangerLight}`, colors.danger + "66"),
            label: "Unbookable",
            desc: "won't book — past window / outside permissions",
        },
    ];
    const origins = ["rule", "reconciled", "manual"];
    return (_jsxs("div", { style: { ...style }, children: [_jsx("div", { style: { display: "flex", flexWrap: "wrap", gap: 12 }, children: items.map((it) => (_jsxs("span", { title: it.desc, style: { display: "inline-flex", alignItems: "center", gap: 6 }, children: [_jsx("span", { style: it.sw }), _jsx("span", { style: { fontSize: "0.72rem", color: colors.textSecondary }, children: it.label })] }, it.label))) }), _jsxs("div", { style: { display: "flex", flexWrap: "wrap", gap: 12, marginTop: 6, alignItems: "center" }, children: [_jsx("span", { style: { fontSize: "0.68rem", color: colors.textTertiary }, children: "Booked by:" }), origins.map((o) => (_jsxs("span", { title: ORIGIN_BADGE[o].desc, style: { display: "inline-flex", alignItems: "center", gap: 4 }, children: [_jsx(Icon, { name: ORIGIN_BADGE[o].icon, size: 11, color: ORIGIN_BADGE[o].color }), _jsx("span", { style: { fontSize: "0.72rem", color: colors.textSecondary }, children: ORIGIN_BADGE[o].label })] }, o)))] }), _jsx("span", { style: { display: "block", marginTop: 4, fontSize: "0.68rem", color: colors.textTertiary }, children: "Colours identify the court (C1\u2013C4), not the status." })] }));
}
